// app/frontend/pages/Admin/PendingRecords.tsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Head, usePage, PageProps } from '@inertiajs/react';
import { Container, Title, Badge, Text, Space, Grid } from '@mantine/core';
import DynamicDrawer from '../../components/Drawer/DynamicDrawer';
import EventDrawerContent from '../../components/Drawer/EventDrawerContent';
import ArtistDrawerContent from '../../components/Drawer/ArtistDrawerContent';
import EventCard from '../../components/Cards/EventCard';
import ArtistCard from '../../components/Cards/ArtistCard';

interface PendingRecordsProps {
  events: Array<{
    id: number;
    name: string;
    slug: string;
    event_start_date: string;
    event_end_date: string;
    banner_url?: string | null;
  }>;
  artists: Array<{
    id: number;
    name: string;
    slug: string;
    avatar_url?: string | null;
  }>;
  pending_counter: number;
}

interface Organizer {
  id: number;
  name: string;
  slug: string;
  description?: string;
  website_url?: string;
  facebook_url?: string;
  instagram_url?: string;
}

export default function PendingRecords() {
  const { events, artists, pending_counter } = usePage<PageProps & { props: PendingRecordsProps }>().props;

  // type your state explicitly so TS knows what to feed the drawer
  const [selectedEvent, setSelectedEvent] = React.useState<PendingRecordsProps['events'][0] | null>(null);
  const [selectedArtist, setSelectedArtist] = React.useState<PendingRecordsProps['artists'][0] | null>(null);
  const [drawerOpened, setDrawerOpened] = React.useState(false);
  const [drawerContentType, setDrawerContentType] = React.useState<'event' | 'artist'>('event');
  const [selectedOrganizer, setSelectedOrganizer] = useState<Organizer | null>(null);

  const handleEventClick = (event: PendingRecordsProps['events'][0]) => {
    setSelectedEvent(event);
    setDrawerContentType('event');
    setDrawerOpened(true);
  };

  const handleArtistClick = (artist: PendingRecordsProps['artists'][0]) => {
    setSelectedArtist(artist);
    setDrawerContentType('artist');
    setDrawerOpened(true);
  };

  return (
    <>
      <Head title="Pending Records | WhereCanWeDance.com" />

      <Container size="md" my="xl">
        <Title order={1} mb="lg" c="white" className="rubik">
          Pending Records{' '}
          <Badge
            color="yellow"
            variant="light"
            style={{
              fontSize: '2.25rem',
              padding: '1.5rem 2rem',
              borderRadius: '10px',
              lineHeight: 1,
            }}
          >
            {pending_counter}
          </Badge>
        </Title>

        {/* EVENTS */}
        <Title order={3} mb="lg" c="white" className="poppins">
          Pending Events
        </Title>
        {events.length > 0 ? (
          <Grid mb="lg" gutter="md">
            {events.map((event) => (
              <Grid.Col key={event.id} span={{ base: 12, sm: 4, lg: 4 }}>
                <EventCard event={event} onCardClick={() => handleEventClick(event)} />
              </Grid.Col>
            ))}
          </Grid>
        ) : (
          <Text color="dimmed">No pending events.</Text>
        )}

        <Space h="xl" />

        {/* ARTISTS */}
        <Title order={3} mb="lg" c="white" className="poppins">
          Pending Artists
        </Title>
        {artists.length > 0 ? (
          <Grid mb="lg" gutter="md">
            {artists.map((artist) => (
              <Grid.Col key={artist.id} span={{ base: 6, sm: 3, md: 3 }}>
                <ArtistCard artist={artist} onCardClick={() => handleArtistClick(artist)} />
              </Grid.Col>
            ))}
          </Grid>
        ) : (
          <Text color="dimmed">No pending artists.</Text>
        )}
      </Container>

      {/* DYNAMIC DRAWER */}
      <DynamicDrawer
        opened={drawerOpened}
        type={drawerContentType}
        data={
          drawerContentType === 'event'
            ? selectedEvent
            : drawerContentType === 'artist'
              ? selectedArtist
              : selectedOrganizer
        }
        onClose={() => setDrawerOpened(false)}
      />
    </>
  );
}
